import { Grid } from "@material-ui/core";
import { useEffect, Fragment } from "react";
import { Controller } from "react-hook-form";
import { useSelector } from "react-redux";
import styled from "styled-components";
import { PrimaryButton } from "../../../../../components/button";
import { DateField } from "../../../../../components/forms/date";
import { RadioGroup } from "../../../../../components/forms/radio";
import { Select } from "../../../../../components/forms/select";
import { TextField } from "../../../../../components/forms/text";
import { Icon } from "../../../../../components/icons";
import { AppStore } from "../../../../../store";
import { CryptoDTO } from "../../../../../store/crypto/models/dto";
import {
  BlockChainTypeEnum,
  TypeOfFraudEnum,
} from "../../../../../store/crypto/models/internal";
import { colours } from "../../../../../styles/colours";
import { mapObject } from "../../../../../utils/utils";
import { Container, StyledWidget } from "../components";
import { useStepValidation } from "../step";
import { useList } from "../../../../../components/hooks/use-list";
import { NumberField } from "../../../../../components/forms/number";
import { useTranslation } from "react-i18next";

const GridItem = styled(Grid).attrs({ item: true })`
  display: flex;
  align-items: center;
`;

const CloseIcon = styled(Icon).attrs({
  type: "close",
  colour1: colours.primaryBlue,
})`
  font-size: 1.3em;
`;

const OtherTextField = styled(TextField)`
  margin-top: 1em;
`;

const { defaults, useFields } = CryptoDTO.Requests.CreateFraudReport;

export const TransactionDetails: React.FunctionComponent<{}> = () => {
  const defaultData = useSelector<
    AppStore,
    AppStore["crypto"]["newReportForm"]
  >((store) => store.crypto.newReportForm);

  const { labels } = useFields();

  const [
    transactions,
    setTransaction,
    addTransaction,
    removeTransaction,
    canAddMore,
  ] = useList(
    { reference: "", transactionDate: null },
    defaultData.transactions
  );

  const {
    control,
    watch,
    setValue,
    clearErrors,
    formState: { errors },
  } = useStepValidation(
    [
      "blockchain",
      "address",
      "transactions",
      "fraudType",
      "fraudAmount",
      "fraudCurrency",
      "otherFraudDescription",
    ],
    { fraudAmount: ("" as any) as undefined }
  ); // to avoid setting an amount to 0

  useEffect(() => {
    setValue(
      "transactions",
      transactions.filter(
        ({ reference, transactionDate }) =>
          !(reference === "" && transactionDate === null)
      )
    );
  }, [transactions]);

  const fraudType = watch("fraudType");
  const chain = watch("blockchain");

  useEffect(() => {
    setValue("fraudCurrency", chain === "ETH" ? "" : chain);
    clearErrors("address");
  }, [chain]);

  const { t } = useTranslation("blockchain-tools");

  return (
    <Container>
      <StyledWidget title={t("enter-blockchain-and-address-of-victim")}>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={3}>
            <Controller
              control={control}
              name="blockchain"
              defaultValue={defaults.blockchain}
              render={({ field }) => (
                <Select
                  label={t("chain", { ns: "fields" })}
                  fullWidth
                  options={mapObject(BlockChainTypeEnum.enum, (value, key) => ({
                    title: value,
                    value: key,
                  }))}
                  {...field}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={9}>
            <Controller
              control={control}
              name="address"
              defaultValue={defaults.address}
              render={({ field, fieldState: { error } }) => (
                <TextField
                  fullWidth
                  label={labels.address}
                  error={!!error}
                  helperText={error?.message}
                  {...field}
                />
              )}
            />
          </Grid>
        </Grid>
      </StyledWidget>
      <StyledWidget title={t("transaction-details")}>
        <Grid container spacing={3}>
          {transactions.map((tx, i) => (
            <Fragment key={i}>
              <Grid item xs={12} sm={4}>
                <DateField
                  label={t("transaction-date", { ns: "fields" })}
                  fullWidth
                  onChange={(newDate) =>
                    setTransaction(i, { ...tx, transactionDate: newDate })
                  }
                  value={tx.transactionDate}
                  error={
                    !!errors[
                      (`transactions[${i}].transactionDate` as unknown) as "transactions"
                    ]
                  }
                  helperText={
                    (errors[
                      (`transactions[${i}].transactionDate` as unknown) as "transactions"
                    ] as Error | undefined)?.message
                  }
                />
              </Grid>
              <Grid item xs={10} sm={7}>
                <TextField
                  fullWidth
                  value={tx.reference}
                  onChange={(e) =>
                    setTransaction(i, {
                      ...tx,
                      reference: e.target.value,
                    })
                  }
                  label={t("transaction-reference")}
                  error={
                    !!errors[
                      (`transactions[${i}].reference` as unknown) as "transactions"
                    ]
                  }
                  helperText={
                    (errors[
                      (`transactions[${i}].reference` as unknown) as "transactions"
                    ] as Error | undefined)?.message
                  }
                />
              </Grid>
              <GridItem xs={2} sm={1}>
                <CloseIcon onClick={() => removeTransaction(i)} />
              </GridItem>
            </Fragment>
          ))}
          <Grid item xs={12}>
            <PrimaryButton
              icon="arrowDown"
              onClick={addTransaction}
              disabled={!canAddMore}
            >
              {t("add-more")}
            </PrimaryButton>
          </Grid>
        </Grid>
      </StyledWidget>
      <StyledWidget title={t("select-type-of-fraud")}>
        <Controller
          control={control}
          name="fraudType"
          defaultValue={defaults.fraudType}
          render={({ field }) => (
            <RadioGroup<typeof TypeOfFraudEnum.T>
              options={mapObject(TypeOfFraudEnum.enum, (val, key) => ({
                id: key,
                label: val,
              }))}
              {...field}
            />
          )}
        />
        {fraudType === "OTHER" ? (
          <Controller
            control={control}
            name="otherFraudDescription"
            defaultValue={defaults.otherFraudDescription}
            render={({ field, fieldState: { error } }) => (
              <OtherTextField
                fullWidth
                label={labels.otherFraudDescription}
                error={!!error}
                helperText={error?.message}
                {...field}
              />
            )}
          />
        ) : null}
      </StyledWidget>
      <StyledWidget title={t("total-amount-defrauded")}>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={3}>
            <Controller
              control={control}
              name="fraudCurrency"
              render={({ field, fieldState: { error } }) => (
                <TextField
                  fullWidth
                  label={labels.fraudCurrency}
                  {...field}
                  disabled={chain !== "ETH"}
                  error={!!error}
                  helperText={error?.message}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={9}>
            <Controller
              control={control}
              name="fraudAmount"
              defaultValue={undefined}
              render={({ field, fieldState: { error } }) => (
                <NumberField
                  fullWidth
                  label={labels.fraudAmount}
                  error={!!error}
                  helperText={error?.message}
                  {...field}
                />
              )}
            />
          </Grid>
        </Grid>
      </StyledWidget>
    </Container>
  );
};
